import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import log from 'loglevel';

import { useInboxEntryCommentsSlice } from './useInboxEntryCommentsSlice';
import { useDeleteComment } from '../../services/inboxEntryComments.service';

export const useDeleteInboxEntryComment = (inboxEntryId: string) => {
  const { t } = useTranslation();
  const { deleteComment: deleteCommentService } = useDeleteComment();

  const {
    deletingCommentId,
    editingCommentId,
    setDeletingCommentId,
    setEditingCommentId,
    removeComment,
    setError,
  } = useInboxEntryCommentsSlice(inboxEntryId);

  const deleteComment = useCallback(
    async (commentId: string) => {
      setDeletingCommentId(commentId);
      setError(null);

      try {
        const success = await deleteCommentService({ inboxEntryId, commentId });

        if (success) {
          removeComment(commentId);

          if (editingCommentId === commentId) {
            setEditingCommentId(null);
          }
        }

        return success;
      } catch (error) {
        log.error(
          `Error deleting comment ${commentId} for inbox entry ${inboxEntryId}:`,
          error,
        );
        setError(t('components.inboxEntryDetailsModal.comments.deleteError'));
        throw error;
      } finally {
        setDeletingCommentId(null);
      }
    },
    [
      inboxEntryId,
      editingCommentId,
      deleteCommentService,
      removeComment,
      setDeletingCommentId,
      setEditingCommentId,
      setError,
    ],
  );

  return { deleteComment, deletingCommentId };
};
